import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PortalDeterminationService } from '@core/services/portal-determination.service';
import { SpinnerService } from '@core/services/spinner.service';
import { ProgramDetail } from '@core/typings/program.typing';
import { ApplicationEligibilityService } from '@features/application-eligibility/application-eligibility.service';
import { ApplicationEligibility } from '@features/application-eligibility/application-eligibility.typing';
import { ApplicationViewService } from '@features/application-view/application-view.service';
import { ApplicationApplicantService } from '@features/apply/application-applicant.service';
import { ProgramService } from '@features/programs/services/program.service';
import { I18nService } from '@yourcause/common/i18n';
import { ConfirmAndTakeActionService } from '@yourcause/common/modals';
import { NotifierService } from '@yourcause/common/notifier';
import { CopyApplicationResources } from './copy-application.resources';

@Injectable({ providedIn: 'root' })
export class CopyApplicationService {

  constructor (
    private confirmAndTakeActionService: ConfirmAndTakeActionService,
    private i18n: I18nService,
    private copyApplicationResources: CopyApplicationResources,
    private spinnerService: SpinnerService,
    private applicationEligibilityService: ApplicationEligibilityService,
    private portal: PortalDeterminationService,
    private programService: ProgramService,
    private applicationViewService: ApplicationViewService,
    private router: Router,
    private applicationApplicantService: ApplicationApplicantService,
    private notifier: NotifierService
  ) { }

  getCopySuccessMessage (isNomination: boolean) {
    return this.i18n.translate(
      isNomination ? 'common:textSuccessCopyNom' : 'common:textSuccessCopyApp',
      {},
      isNomination ?
        'Successfully copied the nomination' :
        'Successfully copied the application'
    );
  }

  getCopyErrorMessage (isNomination: boolean) {
    return this.i18n.translate(
      isNomination ?
        'common:textErrorCopyNom' :
        'common:textErrorCopyApp',
      {},
      isNomination ?
        'There was an error copying the nomination' :
        'There was an error copying the applicatioin'
    );
  }

  /**
   * Handles Copying an Application or Nomination
   *
   * @param applicationIdToCopy: App ID to Copy
   * @param grantProgramId: Program ID
   * @param cycleId: Cycle ID
   * @param organizationId: Org ID
   * @param isNomination: Is Nomination?
   * @param isProgramPage: Program Page does validation and confirmation prior to calling this function
   * @param clientId: Client ID
   * @returns the ID of the newly created app (if it passed)
   */
  async handleCopyApplication (
    applicationIdToCopy: number,
    grantProgramId: number,
    cycleId: number,
    organizationId: string|number,
    isNomination: boolean,
    isProgramPage: boolean,
    clientId?: number
  ) {
    let program: ProgramDetail;
    let copyResponse: ApplicationEligibility.CopyApplicationResponse;
    this.spinnerService.startSpinner();
    const canProceed = await this.checkCopyEligibility(
      applicationIdToCopy,
      grantProgramId,
      cycleId,
      organizationId,
      isNomination
    );
    this.spinnerService.stopSpinner();
    if (canProceed) {
      if (this.portal.isApply) {
        this.spinnerService.startSpinner();
        program = await this.programService.getAndAdaptProgramForApplicant(grantProgramId);
        this.spinnerService.stopSpinner();
        if (!!program.eligibilityForm) {
          // Navigate to eligiblity form
          this.router.navigate([`apply/programs/${program.grantProgramId}/apply`], {
            queryParams: {
              copyOfId: applicationIdToCopy
            }
          });

          return;
        } else {
          copyResponse = await this.proceedWithCopy(
            applicationIdToCopy,
            isNomination,
            !isProgramPage,
            clientId
          );
        }
      } else {
        copyResponse = await this.proceedWithCopy(
          applicationIdToCopy,
          isNomination,
          !isProgramPage,
          clientId
        );
      }
      if (!!copyResponse?.applicationId) {
        this.notifier.success(this.getCopySuccessMessage(isNomination));
        const newAppId = copyResponse.applicationId;
        let route = '';
        if (this.portal.isApply) {
          route = `/apply/application/${newAppId}/forms/${program.form.formId}`;
        } else {
          route = `/management/manage-${isNomination ? 'nominations/' : 'applications/'}${
            isNomination ? 'nomination' : 'application'}/` + newAppId + '/form/no-form';
        }
        this.router.navigate([route]);
      } else {
        this.notifier.error(this.getCopyErrorMessage(isNomination));
      }
    }
  }

  async doCopy (
    applicationIdToCopy: number,
    clientId: number
  ) {
    const response = await this.copyApplicationResources.copyApplication(
      applicationIdToCopy,
      clientId
    );
    if (!!response.applicationId) {
      if (this.portal.isApply) {
        await this.applicationApplicantService.getMyApplications(true);
      }
    }

    return response;
  }

  async proceedWithCopy (
    applicationIdToCopy: number,
    isNomination: boolean,
    showConfirmationModal: boolean,
    clientId?: number
  ) {
    let response;
    if (!showConfirmationModal) {
      response = await this.confirmAndTakeActionService.genericTakeAction(
        () => this.doCopy(applicationIdToCopy, clientId),
        '',
        this.getCopyErrorMessage(isNomination)
      );
    } else {
      response = await this.confirmAndTakeActionService.genericConfirmAndTakeAction(
        () => this.doCopy(applicationIdToCopy, clientId),
        this.i18n.translate(
          isNomination ? 'common:hdrCopyNomination' : 'common:hdrCopyApplication',
          {},
          isNomination ? 'Copy Nomination' : 'Copy Application'
        ),
        this.i18n.translate(
          isNomination ? 'GLOBAL:textNominationID' : 'common:textApplicationId',
          {},
          isNomination ? 'Nomination ID' : 'Application ID'
        ) + ': ' + applicationIdToCopy,
        this.i18n.translate(
          isNomination ? 'common:textAreYouSureCopyApp' : 'common:textAreYouSureCopyApp',
          {},
          isNomination ?
            'Are you sure you want to copy this nomination?' :
            'Are you sure you want to copy this application?'
        ),
        this.i18n.translate('common:textCopy', {}, 'Copy'),
        '',
        this.getCopyErrorMessage(isNomination)
      );
    }
    if (response.passed) {
      return response.endpointResponse;
    }

    return null;
  }

  /**
   * Is the app eligible to be copied?
   *
   * @param applicationIdToCopy: App ID to Copy
   * @param grantProgramId: Program ID
   * @param cycleId: Cycle ID
   * @param organizationId: Org ID
   * @param isNomination: Is nomination?
   * @returns if the app is eligible to be copied
   */
  async checkCopyEligibility (
    applicationIdToCopy: number,
    grantProgramId: number,
    cycleId: number,
    organizationId: string|number,
    isNomination: boolean
  ) {
    let valid = false;
    if (this.portal.isApply) {
      valid = await this.applicationEligibilityService.checkCopyEligibilityForApplicantView(
        applicationIdToCopy,
        grantProgramId,
        isNomination,
        organizationId as number
      );
    } else {
      const [
        program,
        detail
      ] = await Promise.all([
        this.programService.getProgram('' + grantProgramId),
        this.applicationViewService.getApplicationDetail(applicationIdToCopy)
      ]);
      valid = await this.applicationEligibilityService.handleApplicationEligibilityScenario(
        {
          applicationId: applicationIdToCopy,
          grantProgramCycleId: cycleId,
          grantProgramId,
          orgIdentification: organizationId as string,
          charityBucketId: program.charityBucketId,
          clientId: program.clientId,
          latestVettingRequestStatusForOrg: detail.latestVettingRequestStatusForOrg
        },
        ApplicationEligibility.ApplicationEligibilityScenario.CopyApplication,
        isNomination
      );
    }

    return valid;
  }
}
